import { useRef, useState, useEffect, useCallback } from "react";
import * as icons from "lucide-react";
import { Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { CATEGORIES } from "../../data/categories";

export default function CategoryBar({
  activeCategory,
  onCategoryChange,
  onOpenFilters,
  filterCount = 0,
}) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  function scrollBy(direction) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 240, behavior: "smooth" });
  }

  return (
    <div className="sticky top-[72px] z-40 bg-white border-b border-border">
      <div className="max-w-[1760px] mx-auto px-6 flex items-center">
        {/* Left chevron */}
        {canScrollLeft && (
          <button
            className="flex-shrink-0 mr-2 rounded-full p-1.5 border border-border bg-white hover:bg-subtle shadow-card transition-colors"
            onClick={() => scrollBy(-1)}
            aria-label={t("categoryBar.scrollLeftAria")}
          >
            <ChevronLeft size={16} className="text-dark" />
          </button>
        )}

        {/* Scroll area */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto hide-scrollbar flex-1 min-w-0"
        >
          {CATEGORIES.map((cat) => {
            const LucideIcon = icons[cat.icon];
            const isActive = activeCategory === cat.id;

            return (
              <button
                key={cat.id}
                className={`flex flex-col items-center gap-1 cursor-pointer min-w-fit pb-3 pt-4 border-b-2 transition-colors ${
                  isActive
                    ? "border-dark text-dark"
                    : "border-transparent text-muted hover:text-dark hover:border-border"
                }`}
                // Toggle: clic na activa volve a "all" (limpia filtro).
                // Antes quedaba marcada permanentemente — sen forma de quitala.
                onClick={() => onCategoryChange(isActive ? "all" : cat.id)}
                aria-pressed={isActive}
                title={isActive ? t("categoryBar.removeFilterTitle") : t(`categories.${cat.id}`)}
              >
                {LucideIcon ? (
                  <LucideIcon size={24} aria-hidden="true" />
                ) : (
                  <span className="w-6 h-6" aria-hidden="true" />
                )}
                <span className="text-xs font-medium whitespace-nowrap">
                  {t(`categories.${cat.id}`)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right chevron */}
        {canScrollRight && (
          <button
            className="flex-shrink-0 ml-2 rounded-full p-1.5 border border-border bg-white hover:bg-subtle shadow-card transition-colors"
            onClick={() => scrollBy(1)}
            aria-label={t("categoryBar.scrollRightAria")}
          >
            <ChevronRight size={16} className="text-dark" />
          </button>
        )}

        {/* Filters button */}
        <button
          className="flex items-center gap-2 border border-border rounded-xl px-4 py-2.5 text-sm font-semibold text-dark hover:bg-subtle ml-4 shrink-0 relative transition-colors"
          onClick={onOpenFilters}
          aria-label={
            filterCount > 0
              ? t("categoryBar.filtersAriaWithCount", { count: filterCount })
              : t("categoryBar.filtersAria")
          }
        >
          <Filter size={16} aria-hidden="true" />
          <span>{t("categoryBar.filtersButton")}</span>
          {filterCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-rauxa text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
              {filterCount > 9 ? "9+" : filterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
